import { Timezone } from '@/modules/common/constants/meeting-timezone.ts';
import { utcIsoToMeetingDateTime } from '@/modules/common/helpers/meetingDateTime.ts';
import {
  MeetingDayOfWeek,
  MeetingRecurrencePatternType,
  MeetingRecurrenceRangeType,
  ScheduleMeetingAttendee,
  ScheduleMeetingRecurrence,
  ScheduleMeetingRequest,
  ScheduledMeetingListItem,
} from 'compliance-api';

export type ScheduleMeetingFormValues = ScheduleMeetingRequest & {
  notify_attendees?: boolean;
};

interface MeetingUpdateChanges {
  hasSimpleChanges: boolean;
  hasTimeChanges: boolean;
  hasMeetingTypeChanges: boolean;
  hasAttendeesChanges: boolean;
  hasRecurrenceChanges: boolean;
  isNotifyForced: boolean;
  canChooseNotifyAttendees: boolean;
}

const getAttendeeKey = (attendee: ScheduleMeetingAttendee) => {
  if (attendee.user_id) return `user:${attendee.user_id}`;
  return `email:${attendee.email?.trim().toLowerCase() || ''}`;
};

const getAttendeeKeys = (attendees: ScheduleMeetingAttendee[] = []) => {
  return attendees.map(getAttendeeKey).filter(Boolean).sort().join('|');
};

const getItemAttendeeKeys = (item?: ScheduledMeetingListItem) => {
  return getAttendeeKeys([
    ...(item?.attendee_users || []).map(user => ({ user_id: user.id })),
    ...(item?.external_attendee_emails || []).map(email => ({ email })),
  ]);
};

const isSameMeetingDateTime = (left?: string, right?: string, timeZone?: string | null) => {
  if (!left || !right) return left === right;
  return (
    utcIsoToMeetingDateTime(left, timeZone).valueOf() ===
    utcIsoToMeetingDateTime(right, timeZone).valueOf()
  );
};

export const normalizeRecurrenceForCompare = (recurrence?: ScheduleMeetingRecurrence | null) => {
  if (!recurrence) return null;

  const patternType = recurrence.pattern_type ?? null;
  const rangeType = recurrence.range_type ?? MeetingRecurrenceRangeType.NoEnd;
  const base = {
    pattern_type: patternType,
    interval: recurrence.interval ?? 1,
    first_day_of_week: recurrence.first_day_of_week ?? MeetingDayOfWeek.Monday,
    range_type: rangeType,
    end_date: rangeType === MeetingRecurrenceRangeType.EndDate ? recurrence.end_date ?? null : null,
    number_of_occurrences:
      rangeType === MeetingRecurrenceRangeType.Numbered
        ? recurrence.number_of_occurrences ?? null
        : null,
  };

  switch (patternType) {
    case MeetingRecurrencePatternType.Weekly:
      return {
        ...base,
        days_of_week: [...(recurrence.days_of_week ?? [])].sort(),
      };
    case MeetingRecurrencePatternType.AbsoluteMonthly:
      return {
        ...base,
        day_of_month: recurrence.day_of_month ?? null,
      };
    case MeetingRecurrencePatternType.RelativeMonthly:
      return {
        ...base,
        days_of_week: [...(recurrence.days_of_week ?? [])].sort(),
        index: recurrence.index ?? null,
      };
    case MeetingRecurrencePatternType.AbsoluteYearly:
      return {
        ...base,
        month: recurrence.month ?? null,
        day_of_month: recurrence.day_of_month ?? null,
      };
    case MeetingRecurrencePatternType.RelativeYearly:
      return {
        ...base,
        month: recurrence.month ?? null,
        days_of_week: [...(recurrence.days_of_week ?? [])].sort(),
        index: recurrence.index ?? null,
      };
    case MeetingRecurrencePatternType.Daily:
    default:
      return base;
  }
};

export const getMeetingUpdateChanges = (
  item: ScheduledMeetingListItem | undefined,
  values: ScheduleMeetingFormValues,
): MeetingUpdateChanges => {
  if (!item) {
    return {
      hasSimpleChanges: false,
      hasTimeChanges: false,
      hasMeetingTypeChanges: false,
      hasAttendeesChanges: false,
      hasRecurrenceChanges: false,
      isNotifyForced: false,
      canChooseNotifyAttendees: false,
    };
  }

  const hasSimpleChanges =
    values.subject !== item.subject ||
    values.body !== item.body ||
    (values.location || '') !== (item.location || '');

  const hasTimeChanges =
    !isSameMeetingDateTime(
      values.start_time,
      item.start_time,
      values.time_zone || item.time_zone,
    ) ||
    !isSameMeetingDateTime(values.end_time, item.end_time, values.time_zone || item.time_zone) ||
    (values.time_zone || Timezone['Australia/Sydney']) !==
      (item.time_zone || Timezone['Australia/Sydney']);

  const hasMeetingTypeChanges = values.is_online_meeting !== item.is_online_meeting;
  const hasAttendeesChanges = getItemAttendeeKeys(item) !== getAttendeeKeys(values.attendees);
  const hasRecurrenceChanges =
    JSON.stringify(normalizeRecurrenceForCompare(values.recurrence)) !==
    JSON.stringify(
      normalizeRecurrenceForCompare(item.recurrence as ScheduleMeetingRecurrence | null),
    );
  const isNotifyForced =
    hasTimeChanges || hasMeetingTypeChanges || hasAttendeesChanges || hasRecurrenceChanges;

  return {
    hasSimpleChanges,
    hasTimeChanges,
    hasMeetingTypeChanges,
    hasAttendeesChanges,
    hasRecurrenceChanges,
    isNotifyForced,
    canChooseNotifyAttendees: hasSimpleChanges && !isNotifyForced,
  };
};
